<template>
  <div class="modal-consulte-bg" :key="refreshCounter" />
  <div
    ref="dialogRef"
    class="modal fade show"
    tabindex="-1"
    role="dialog"
    :key="refreshCounter"
    :aria-labelledby="idTitulo"
    aria-modal="true"
    style="display: unset; border-radius: var(--ct-modal-inner-border-radius)"
    @keydown.esc="close"
    @keydown="manterFocoDentro"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <slot name="header">
            <!-- O `aria-labelledby` do diálogo aponta para ESTE id. Antes era o literal
                 "standard-modalLabel", que não existe em nenhuma tela: a referência
                 quebrada deixava o modal sem nome acessível. -->
            <h4 class="modal-title" :id="idTitulo">{{ titulo }}</h4>
          </slot>
          <button type="button" class="btn-close" @click="close" aria-label="Fechar"></button>
        </div>
        <div class="modal-body">
          <ConsulteGrid
            :columns="rows"
            :data-source="dataSource"
            :actions="true"
            :onlySearchHeader="false"
            :useColumnSort="true"
          >
            <template #header> </template>
            <template #botaoSelecionar="context">
              <button type="button"
                :title="`Selecionar ${context.row.nome}`"
                @click="selecionarRegistro(context.row)"
                class="btn btn-link p-0 border-0 align-baseline font-18 text-success me-2"
                style="margin-right: 5px"
                :aria-label="`Selecionar ${context.row.nome}`"
              >
                <i class="uil-arrow-right" aria-hidden="true"></i>
              </button>
            </template>
          </ConsulteGrid>
        </div>
      </div>
      <!-- /.modal-content -->
    </div>
    <!-- /.modal-dialog -->
  </div>
  <!-- /.modal -->
</template>

<script lang="ts">
  import ConsulteGrid from '@/components/grid/ConsulteGrid.vue';
  import { ConsulteGridColumnsModel } from '@/datasources/consulte-grid';
  import FiltroFuncionarioDataSource from '@/datasources/filtros/funcionario/index.datasource';
  import { FiltroFuncionarioModel } from '@/models/filtros/filtro-funcionario.model';
  import router from '@/routes';
  import sessionService from '@/services/helper/session.service';
  import { useStore } from '@/store';
  import professorService from '@/services/academico/professor/professor.service';
  import eventBus from '@/services/globals/events-bus.service';
  import { ModalActionTypes } from '@/store/modal/action-types';
import {
  abrirDialogo,
  fecharDialogo,
  novoIdTitulo,
  prenderFoco,
} from '@/uteis/dialogo-acessivel';
  import { defineComponent } from 'vue';
  export default defineComponent({
    components: { ConsulteGrid },
    name: 'ModalFiltroFuncionario',
    props: {
      hideFooter: Boolean,
      // quando true, o modal apenas emite o item selecionado via evento 'select' e não altera estado global
      emitOnly: { type: Boolean, default: false },
      // permite sobrescrever o tipo de pesquisa usado pelo datasource/título
      tipoPesquisa: { type: String, default: null },
    },
    mounted() {
      // Este modal e montado por v-if quando a store manda exibir: montar e abrir
      // sao o mesmo momento. `abrirDialogo` guarda o foco anterior, trava a rolagem
      // do fundo e leva o foco para o primeiro campo util.
      this.$nextTick(() => {
        this.elementoAnterior = abrirDialogo(this.$refs.dialogRef as HTMLElement | undefined);
      });

      const tipo = this.tipoPesquisa || useStore().getters.getTipoPesquisaFiltroFuncionarios;
      this.titulo = `Selecione o Funcionário (${tipo})`;
      // garantir datasource consistente quando chamado com prop tipoPesquisa
      this.dataSource = new FiltroFuncionarioDataSource(0, 5, 0, 0, [], tipo);
    },
    data() {
      return {
        // Id do titulo, alvo do `aria-labelledby` do dialogo (era um id fixo,
        // repetido e inexistente). Ver src/uteis/dialogo-acessivel.ts.
        idTitulo: novoIdTitulo('modal-filtro-funcionario'),
        /** Quem tinha o foco antes de abrir, para devolve-lo ao fechar. */
        elementoAnterior: null as HTMLElement | null,
        refreshCounter: 0,
        titulo: '',
        dataSource: new FiltroFuncionarioDataSource(
          0,
          5,
          0,
          0,
          [],
          useStore().getters.getTipoPesquisaFiltroFuncionarios,
        ),
        rows: [
          {
            text: 'Selecionar',
            align: 'center',
            cellsalign: 'center',
            datafield: 'botaoSelecionar',
            editable: false,
            filtertype: 'text',
            filterable: false,
          },
          {
            text: 'Código',
            align: 'center',
            cellsalign: 'center',
            datafield: 'codigoFuncionario',
            editable: false,
            filtertype: 'text',
            filterable: true,
          },
          {
            text: 'Identificação',
            align: 'center',
            cellsalign: 'center',
            datafield: 'identificacao',
            editable: false,
            filtertype: 'text',
            filterable: true,
          },
          {
            text: 'Nome',
            align: 'center',
            cellsalign: 'center',
            datafield: 'nome',
            filtertype: 'text',
            filterable: true,
          },
        ] as ConsulteGridColumnsModel[],
      };
    },
    /**
     * Rede de seguranca: ha fluxos que removem o modal sem passar pelo close()
     * (troca de rota com o modal aberto, por exemplo). Sem isto a rolagem do
     * fundo ficaria travada e o foco perdido no <body>.
     */
    unmounted() {
      fecharDialogo(this.elementoAnterior);
    },
    methods: {
      /** Prende o Tab dentro do dialogo. Ver src/uteis/dialogo-acessivel.ts. */
      manterFocoDentro(evento: KeyboardEvent) {
        prenderFoco(evento, this.$refs.dialogRef as HTMLElement | undefined);
      },
      close() {
        fecharDialogo(this.elementoAnterior);
        this.elementoAnterior = null;
        this.refreshCounter += 1;
        this.$emit('close');
      },
      async selecionarRegistro(row: FiltroFuncionarioModel) {
        const codigo = parseInt(row.codigoFuncionario?.toString() ?? '0');
        const nome = row.nome?.toString() ?? '';

        if (this.emitOnly) {
          // apenas retorna seleção para o pai via evento e não altera estado global
          this.$emit('select', { codigo, nome });
          this.$emit('close');
          return;
        }

        useStore().dispatch(
          ModalActionTypes.ACTION_SET_CODIGOPROFESSOR_SELECIONADO_TELA_FILTRO,
          codigo,
        );
        useStore().dispatch(
          ModalActionTypes.ACTION_SET_NOMEPROFESSOR_SELECIONADO_TELA_FILTRO,
          nome,
        );

        // Os serviços singleton guardam o código do professor em campo próprio: sem reler o store
        // aqui, a tela que abrir em seguida ainda usaria o professor anterior.
        professorService.initialize();
        eventBus.emit('professorSelecionadoAlterado');

        const rotaDestino = useStore().getters.getRotaChamarFiltroFuncionarios;

        if (sessionService.IsProfessor()) {
          await router.replace({ path: '/homeprofessor' });
        } else {
          await router.replace({ path: '/home' });
        }

        if (rotaDestino == 'homeprofessor') await router.replace({ path: rotaDestino });
        else await router.push({ name: rotaDestino });
        this.$emit('close');
      },
    },
  });
</script>
<style scoped>
  /*
   * `scoped`: estas regras eram globais e, como este componente é importado no MainContent,
   * valiam para TODO modal do sistema. No celular o `max-width: 100% !important` anulava o
   * limite do ModalConsulte (`calc(100vw - 1.5rem)`) e, somado à margem de 0,5rem do
   * _acessibilidade.scss, empurrava o diálogo 8px para fora da tela.
   */
  @media (max-width: 576px) {
    .modal-dialog {
      max-width: 100% !important;
      margin: 0;
    }

    .modal-content {
      height: 100vh;
    }
  }

  /* Para telas maiores que 576px */
  @media (min-width: 577px) {
    .modal-dialog {
      max-width: 80% !important;
      margin: auto;
    }
  }
</style>

<style>
  .card-header {
    padding: 0px !important;
  }

  .modal-consulte-bg {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1050;
    width: 100%;
    height: 100%;
    background-color: #24053d90;
  }
</style>
